/**
 * Phase 7.2 — per-source per-day history register.
 *
 * The §7.22 Feed Health surface (R8) renders a 30-day history strip per
 * source. The strip is meaningful only if it reflects ACTUAL ingest
 * history — not the synthetic sample data the surface currently falls
 * back to. This module is the persistent-state side: each ingest run
 * appends today's per-source rollup; the surface reads back the last 30
 * days for the strip.
 *
 * Storage:
 *   - Per-app: each app (PaCC, future apps) owns its own history file.
 *     The package only defines the schema + pure update/query functions;
 *     I/O happens in the app's ingest script.
 *   - Lightweight: ~70 sources × 1 byte per FeedState slot × 30 days ≈
 *     2 KB JSON. Cheap to load on every page render.
 *   - Replace-or-append: if today's ISO date already exists, the new
 *     entry REPLACES the old one (re-running ingest within the same day
 *     overwrites; doesn't append duplicates).
 *   - Pruning: entries older than `maxDays` (default 30) get dropped on
 *     every append.
 *
 * The history is derived from `sourceHealth()` output — same 5-state
 * taxonomy (live/idle/stale/rotting/degraded/blocked, plus undefined
 * for sources that didn't exist on a given day).
 */

import { sourceHealth, type SourceHealth, type BlockedReason, type FusedSignal } from './index.js';
import type { SourceReport } from './ingest.js';

/** The 6-state taxonomy from §7.22 R8 — mirrors PaCC's feed-health/types.ts.
 *  Defined here so the history register is self-contained (no app dep).
 *  Encoded as a single uppercase letter for compact serialisation. */
export type SourceState = 'live' | 'idle' | 'stale' | 'rotting' | 'degraded' | 'blocked';

const STATE_TO_CODE: Record<SourceState, string> = {
  live: 'L', idle: 'I', stale: 'S', rotting: 'R', degraded: 'D', blocked: 'B',
};
const CODE_TO_STATE: Record<string, SourceState> = {
  L: 'live', I: 'idle', S: 'stale', R: 'rotting', D: 'degraded', B: 'blocked',
};

/** Derive the canonical SourceState from a SourceHealth row.
 *  Priority: blocked → degraded → rotting → stale → idle → live.
 *  Matches feed-health/types.ts deriveState() in PaCC. */
export function deriveSourceState(h: SourceHealth): SourceState {
  if (h.blocked) return 'blocked';
  if (!h.ok) return 'degraded';
  if (h.rotting) return 'rotting';
  if (h.stale) return 'stale';
  if (h.signalCount === 0) return 'idle';
  return 'live';
}

/** One day's rollup: {sourceId → 1-letter state code}. Compact JSON. */
export interface DailySourceStates {
  /** ISO date (YYYY-MM-DD) the rollup is for — the snapshot's generatedAt date. */
  date: string;
  /** Source id → 1-letter state code (see CODE_TO_STATE). Compact for ~70+ sources. */
  states: Record<string, string>;
}

export interface SourceHistory {
  schemaVersion: 1;
  /** Most-recent-first. */
  days: DailySourceStates[];
}

export const EMPTY_HISTORY: SourceHistory = { schemaVersion: 1, days: [] };

const ONE_DAY_MS = 86_400_000;

function isoDate(t: number | string): string {
  return new Date(t).toISOString().slice(0, 10);
}

/** Append (or replace) today's rollup; prune entries older than `maxDays`.
 *  Pure — returns a NEW SourceHistory; does not mutate the input. */
export function appendDailyEntry(
  history: SourceHistory,
  health: readonly SourceHealth[],
  generatedAtIso: string,
  maxDays: number = 30,
): SourceHistory {
  const date = isoDate(generatedAtIso);
  const states: Record<string, string> = {};
  for (const h of health) {
    states[h.id] = STATE_TO_CODE[deriveSourceState(h)];
  }
  const entry: DailySourceStates = { date, states };

  // Strip any existing entry for this date; prepend the new one.
  const filtered = history.days.filter(d => d.date !== date);
  const next: DailySourceStates[] = [entry, ...filtered];

  // Sort most-recent first (paranoia — `filtered` was already in order if input was).
  next.sort((a, b) => b.date.localeCompare(a.date));

  // Prune: keep entries within maxDays of the NEWEST entry in the merged
  // set (not of the just-added entry — appending an older date shouldn't
  // wipe newer history). Anchors against the actual newest date so the
  // window slides forward only when we add a date past the current max.
  const newestDate = next[0]?.date ?? date;
  // Keep entries strictly newer than `cutoff` so the window holds exactly
  // maxDays days (newest, newest-1, ..., newest-(maxDays-1)).
  const cutoff = Date.parse(newestDate) - maxDays * ONE_DAY_MS;
  const pruned = next.filter(d => Date.parse(d.date) > cutoff);

  return { schemaVersion: 1, days: pruned };
}

/** Convenience: convert a full IngestResult into the next history state.
 *  Most callers use this rather than calling sourceHealth + appendDailyEntry
 *  by hand. */
export function appendIngestResult(
  history: SourceHistory,
  ingest: { generatedAt: string; signals: readonly FusedSignal[]; sources: readonly SourceReport[] },
  blockedSources?: Readonly<Record<string, BlockedReason>>,
  maxDays: number = 30,
): SourceHistory {
  const generatedAtMs = Date.parse(ingest.generatedAt);
  const health = sourceHealth(ingest.signals, ingest.sources, generatedAtMs, blockedSources);
  return appendDailyEntry(history, health, ingest.generatedAt, maxDays);
}

/** Return the last `days` of state for a given source, OLDEST→NEWEST
 *  (the order Feed Health's HistoryStrip renders).
 *
 *  Sources that didn't exist on a given day get the `gap` state — render
 *  as the same as idle (faded; no signal yet to render). Missing days
 *  (no ingest ran that day) also become `gap`. This way the strip's
 *  length is ALWAYS `days`, regardless of register completeness. */
export function getSourceHistory(
  history: SourceHistory,
  sourceId: string,
  days: number = 30,
  /** ISO date the strip ends on; defaults to history's most recent entry,
   *  or today if history is empty. */
  endDate?: string,
): SourceState[] {
  const newest = endDate ?? history.days[0]?.date ?? isoDate(Date.now());
  const newestMs = Date.parse(newest);

  // Index history by date for O(days) lookup.
  const byDate = new Map<string, DailySourceStates>();
  for (const d of history.days) byDate.set(d.date, d);

  const out: SourceState[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const dateMs = newestMs - i * ONE_DAY_MS;
    const date = isoDate(dateMs);
    const entry = byDate.get(date);
    if (!entry) { out.push('idle'); continue; }   // ingest didn't run that day
    const code = entry.states[sourceId];
    if (!code) { out.push('idle'); continue; }    // source didn't exist that day
    out.push(CODE_TO_STATE[code] ?? 'idle');
  }
  return out;
}

/** Summary: how many days back the register actually covers. Used by the
 *  surface to render the "real-history N/30 days" honesty rail. */
export function getHistoryCoverage(history: SourceHistory): {
  days: number;
  earliest: string | null;
  latest: string | null;
} {
  return {
    days: history.days.length,
    earliest: history.days[history.days.length - 1]?.date ?? null,
    latest: history.days[0]?.date ?? null,
  };
}
